var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useState, useContext, createContext } from "react";
import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, } from "@apollo/client";
import { GraphQLError } from "graphql";
import { setContext } from "@apollo/client/link/context";
import { AuthContext } from "./AuthContextProvider";
import { Box, CircularProgress } from "@mui/material";
import { ApolloProvider } from "@apollo/client";
import { SettingsContext } from "./SettingsContextProvider";
import { getOperationRole } from "@/services/Permissions";
import { getTimeZoneValidationMessage } from "@/services/graphqlActionError";
export var defaultApolloContextValues = {
    apolloClient: null,
    role: "admin-user",
};
/**
 * Create the AuthContext using the default values.
 */
export var ApolloContext = createContext(defaultApolloContextValues);
export var ApolloContextProvider = function (_a) {
    var children = _a.children, role = _a.role;
    var _b = useState(null), apolloClient = _b[0], setApolloClient = _b[1];
    var _c = useContext(AuthContext), isAuthenticated = _c.isAuthenticated, getAccessToken = _c.getAccessToken, hasRole = _c.hasRole;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var createApolloClient = function () {
        var httpLink = createHttpLink({
            uri: globalSettings.HASURA_URL,
        });
        var authLink = setContext(function (operation, _a) {
            var headers = _a.headers;
            // get the authentication token from local storage if it exists
            var token = getAccessToken();
            if (!token) {
                console.error("No access token available");
                return {};
            }
            // return the headers to the context so httpLink can read them
            var operationRole = getOperationRole(operation, hasRole);
            return {
                headers: __assign({ "authorization": token ? "Bearer ".concat(token) : "", "x-hasura-role": operationRole }, headers),
            };
        });
        var validationLink = new ApolloLink(function (operation, forward) {
            return forward(operation).map(function (response) {
                var _a;
                return (__assign(__assign({}, response), { errors: (_a = response.errors) === null || _a === void 0 ? void 0 : _a.map(function (error) {
                        var message = getTimeZoneValidationMessage(error);
                        return message
                            ? new GraphQLError(message, {
                                nodes: error.nodes,
                                source: error.source,
                                positions: error.positions,
                                path: error.path,
                                originalError: error.originalError,
                                extensions: error.extensions,
                            })
                            : error;
                    }) }));
            });
        });
        var apolloClient = new ApolloClient({
            link: authLink.concat(validationLink).concat(httpLink),
            cache: new InMemoryCache(),
        });
        return apolloClient;
    };
    useEffect(function () {
        // Clear client if not authenticated (logout case)
        if (!isAuthenticated) {
            if (apolloClient) {
                setApolloClient(null);
            }
            return;
        }
        // Don't recreate if we already have a client
        if (apolloClient) {
            return;
        }
        var token = getAccessToken();
        if (!token) {
            return;
        }
        var newClient = createApolloClient();
        setApolloClient(newClient);
    }, [isAuthenticated, apolloClient, getAccessToken]);
    // Setup the context provider
    return (_jsx(ApolloContext.Provider, { value: {
            apolloClient: apolloClient,
            role: role,
        }, children: children }));
};
export var ApolloWrapper = function (_a) {
    var children = _a.children;
    var apolloClient = useContext(ApolloContext).apolloClient;
    // Show loading spinner while waiting for client
    if (null === apolloClient) {
        return (_jsx(Box, { className: "apollo-wrapper", children: _jsx(CircularProgress, {}) }));
    }
    // Show app content when authenticated and client is ready
    return _jsx(ApolloProvider, { client: apolloClient, children: children });
};
export var CustomApolloContextProvider = function (_a) {
    var children = _a.children, role = _a.role;
    return (_jsx(ApolloContextProvider, { role: role, children: _jsx(ApolloWrapper, { children: children }) }));
};
